/**
 * File Utility Functions
 *
 * Common utilities for file handling in the import modal.
 *
 * @module components/import/utils
 */

/**
 * Format file size for display
 *
 * Converts bytes to human-readable format (KB, MB, GB).
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 *
 * @example
 * ```ts
 * formatFileSize(1024); // "1 KB"
 * formatFileSize(1048576); // "1 MB"
 * formatFileSize(5242880); // "5 MB"
 * ```
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

/**
 * Get file extension from filename
 *
 * @param filename - File name
 * @returns File extension (lowercase, without dot)
 *
 * @example
 * ```ts
 * getFileExtension('scan.csv'); // "csv"
 * getFileExtension('data.JSON'); // "json"
 * ```
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Check if file type is supported
 *
 * @param file - File to check
 * @param supportedExtensions - Array of supported extensions (without dot)
 * @returns Whether file type is supported
 *
 * @example
 * ```ts
 * isFileTypeSupported(file, ['csv', 'json']); // true if file is CSV or JSON
 * ```
 */
export function isFileTypeSupported(
  file: File,
  supportedExtensions: string[]
): boolean {
  const extension = getFileExtension(file.name);
  return supportedExtensions.includes(extension);
}
